import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, finalize, forkJoin, switchMap } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestapiService } from '@app/services/restapi.service';
import { SurveysService } from '@app/services/surveys.service';
import { RestCustomInspectionModel } from '@main-application/boards/interfaces/custom-inspection';
import { ConfirmationModalData } from '@shared/interfaces/modal-data';
import { RestTemplateAreaModel, RestTemplateModel } from '@template/models/rest-template-model.interface';
import {
  AreaModel,
  RestTemplateEditorRatingGroup,
  TemplateEditorEmojiContent,
  TemplateEditorRatingGroup,
  TemplateEditorRatingGroupItem,
  getInitialAreaData,
} from '@template/models/template-editor-model';
import { ModalsService } from '@ui-components/modals/modals.service';

@Injectable({
  providedIn: 'root',
})
export class TemplateEditorService {
  private readonly route = 'inspections/';
  _templatesLoading = new BehaviorSubject<boolean>(false);
  templatesLoading$ = this._templatesLoading.asObservable();
  private _ratingGroups: ReplaySubject<TemplateEditorRatingGroup[]>;
  private _emojiContent: ReplaySubject<TemplateEditorEmojiContent[]>;
  constructor(
    private service: RestapiService,
    private modalsService: ModalsService,
    private surveysService: SurveysService
  ) {}

  getTemplates(preserveInspectionId?: number) {
    this._templatesLoading.next(true);
    return this.service
      .getData<RestTemplateModel[]>(this.route + 'template/list?filterUnavailableTemplates=false')
      .pipe(
        map(templates => {
          if (!templates?.length) {
            return [];
          }
          const filteredTemplates = templates.filter(
            template => template.isAvailable || template.id === preserveInspectionId
          );
          return filteredTemplates.sort((a, b) => {
            if (a.name === b.name) return 0;
            return a.name > b.name ? 1 : -1;
          });
        }),
        finalize(() => {
          this._templatesLoading.next(false);
        })
      );
  }

  getRatingGroups() {
    if (!this._ratingGroups) {
      this._ratingGroups = new ReplaySubject<TemplateEditorRatingGroup[]>(1);
      this.service
        .getData<RestTemplateEditorRatingGroup[]>(this.route + 'ratingGroups')
        .pipe(
          map(ratingGroups => {
            return ratingGroups.map(group => {
              const { itemsJson, ...rest } = group;
              return { ...rest, items: JSON.parse(itemsJson) as TemplateEditorRatingGroupItem[] };
            });
          })
        )
        .subscribe({
          next: ratingGroups => {
            this._ratingGroups.next(ratingGroups);
          },
          error: error => this._ratingGroups.error(error),
        });
    }
    return this._ratingGroups.asObservable();
  }

  getEmojiContent() {
    if (!this._emojiContent) {
      this._emojiContent = new ReplaySubject<TemplateEditorEmojiContent[]>(1);
      this.service.getData<TemplateEditorEmojiContent[]>('emojiContent').subscribe({
        next: emojiContent => {
          this._emojiContent.next(emojiContent);
        },
        error: error => this._emojiContent.error(error),
      });
    }
    return this._emojiContent.asObservable();
  }

  getTemplate(templateId: number) {
    return this.service.getData<RestTemplateModel>(this.route + 'template/' + templateId);
  }

  editTemplate(inspectionId: number, data: RestCustomInspectionModel) {
    return this.service.post<RestCustomInspectionModel>(
      this.route + 'template/editCustomInspection/' + inspectionId,
      data
    );
  }

  addArea(title: string, templateId: number) {
    const area: RestTemplateAreaModel = {
      title,
      inspectionTemplateId: templateId,
      areaSurveyJson: JSON.stringify(getInitialAreaData(title)),
    } as RestTemplateAreaModel;
    return this.surveysService
      .add(area)
      .pipe(switchMap(id => this.surveysService.get(id).pipe(map(area => [id, area] as const))));
  }

  removeArea(id: number) {
    return this.surveysService.delete(id);
  }

  updateArea(area: AreaModel) {
    const { areaSurvey, ...rest } = area;
    const updatedArea: RestTemplateAreaModel = { ...rest, areaSurveyJson: JSON.stringify(areaSurvey) };
    return this.surveysService.update(updatedArea);
  }

  updateAreas(areas: AreaModel[]) {
    return forkJoin(areas.map(area => this.updateArea(area)));
  }

  openRemovalTemplateModal(inspectionTemplate: RestTemplateModel, data: ConfirmationModalData = {}) {
    return this.modalsService.openConfirmationModal(
      {
        ...{
          title: `Delete ${inspectionTemplate.name} template?`,
          titleCss: 'fade-truncate',
          content: `This will delete template. Are you feeling this confident? 💪`,
          cancelLabel: 'Yikes, no!',
          confirmLabel: 'Yes, delete it',
          confirmColor: 'warn',
        },
        ...data,
      },
      360
    );
  }
}
